import React, { useState } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToSection = (id) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsOpen(false);
    setIsServicesOpen(false);
  };

  const handleLogoClick = () => {
    navigate('/');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const services = [
    { name: 'Web Development', slug: 'web-development' },
    { name: 'UI/UX Design', slug: 'ui-ux-design' },
    { name: 'Blockchain Development', slug: 'blockchain-development' },
    { name: 'E-commerce Development', slug: 'ecommerce-development' },
    { name: 'Custom Software Development', slug: 'custom-software-development' },
    { name: 'Mobile App Development', slug: 'mobile-app-development' },
    { name: '3D Animation', slug: '3d-animation' },
    { name: 'Cloud Solutions', slug: 'cloud-solutions' },
    { name: 'Digital Marketing', slug: 'digital-marketing' },
    { name: 'Creative Copywriting', slug: 'creative-copywriting' }
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-md">
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between h-24 md:h-28">
          {/* Logo */}
          <div className="flex-shrink-0">
            <button 
              onClick={handleLogoClick}
              className="flex items-center hover:opacity-80 transition-opacity duration-300"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_us-tech-services/artifacts/scaqfivo_Logo%20black.png" 
                alt="Code Commerce Logo" 
                className="h-16 md:h-20 w-auto"
              />
            </button>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            {/* Home */}
            <button
              onClick={() => scrollToSection('hero')}
              className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-300 relative group"
            >
              Home
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-600 to-indigo-600 group-hover:w-full transition-all duration-300"></span>
            </button>

            {/* About */}
            <button
              onClick={() => navigate('/about')}
              className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-300 relative group"
            >
              About
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-600 to-indigo-600 group-hover:w-full transition-all duration-300"></span>
            </button>
            
            {/* Services Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setIsServicesOpen(true)}
              onMouseLeave={() => setIsServicesOpen(false)}
            >
              <button
                onClick={() => scrollToSection('services')}
                className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-300 relative group flex items-center space-x-1"
              >
                <span>Services</span>
                <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${isServicesOpen ? 'rotate-180' : ''}`} />
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-600 to-indigo-600 group-hover:w-full transition-all duration-300"></span>
              </button>
              
              {/* Dropdown Menu */}
              {isServicesOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-2xl border-2 border-blue-100 py-2 animate-fade-in">
                  {services.map((service) => (
                    <button
                      key={service.slug}
                      onClick={() => {
                        navigate(`/service/${service.slug}`);
                        setIsServicesOpen(false);
                      }}
                      className="w-full text-left px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors duration-200 font-medium"
                    >
                      {service.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Blog */}
            <button
              onClick={() => navigate('/blog')}
              className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-300 relative group"
            >
              Blog
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-600 to-indigo-600 group-hover:w-full transition-all duration-300"></span>
            </button>

            {/* Contact */}
            <button
              onClick={() => navigate('/contact')}
              className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-300 relative group"
            >
              Contact
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-600 to-indigo-600 group-hover:w-full transition-all duration-300"></span>
            </button>
            
            <Button 
              onClick={() => navigate('/contact')}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Get Started
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-700 hover:text-blue-600"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden pb-6">
            <div className="flex flex-col space-y-4">
              {/* Home */}
              <button
                onClick={() => scrollToSection('hero')}
                className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-300 text-left px-4 py-2 rounded-lg hover:bg-blue-50"
              >
                Home
              </button>

              {/* About */}
              <button
                onClick={() => {
                  navigate('/about');
                  setIsOpen(false);
                }}
                className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-300 text-left px-4 py-2 rounded-lg hover:bg-blue-50"
              >
                About
              </button>
              
              {/* Mobile Services Dropdown */}
              <div>
                <button
                  onClick={() => setIsServicesOpen(!isServicesOpen)}
                  className="w-full text-gray-700 hover:text-blue-600 font-medium transition-colors duration-300 text-left px-4 py-2 rounded-lg hover:bg-blue-50 flex items-center justify-between"
                >
                  <span>Services</span>
                  <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${isServicesOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isServicesOpen && (
                  <div className="mt-2 ml-4 space-y-2">
                    {services.map((service) => (
                      <button
                        key={service.slug}
                        onClick={() => {
                          navigate(`/service/${service.slug}`);
                          setIsOpen(false);
                          setIsServicesOpen(false);
                        }}
                        className="w-full text-left px-4 py-2 text-gray-600 hover:text-blue-600 transition-colors duration-200 text-sm"
                      >
                        {service.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Blog */}
              <button
                onClick={() => {
                  navigate('/blog');
                  setIsOpen(false);
                }}
                className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-300 text-left px-4 py-2 rounded-lg hover:bg-blue-50"
              >
                Blog
              </button>

              {/* Contact */}
              <button
                onClick={() => {
                  navigate('/contact');
                  setIsOpen(false);
                }}
                className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-300 text-left px-4 py-2 rounded-lg hover:bg-blue-50"
              >
                Contact
              </button>
              
              <Button 
                onClick={() => navigate('/contact')}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white w-full rounded-full shadow-lg"
              >
                Get Started
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;